import { AbstractState } from '@sancare/typescript-commons/src/store/types'
import { HealthDataType } from '@sancare/typescript-commons/src/types/health-data'
import { Dayjs } from 'dayjs'

import { FPP_DIAGNOSIS_EXPIRATION_DATE } from '@/misc/constants'
import { StaySuggestionSearch } from '@/misc/types'
import { BaseAct } from '@/store/modules/medical-act/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { Search } from '@/store/modules/stay-saved-search/types'

export enum PredictedLabelType {
  AE = 'AE',
  DP = 'DP',
  DR = 'DR',
  DAS = 'DAS',
  MEDICAL_ACTS = 'medicalActs',
  MMP = 'MMP',
}

export type PredictionModelType = 'classification' | 'generative' | 'agent'

export interface PredictionModel {
  name: string
  type: PredictionModelType
  version: string
  trainedAt?: string
}

export interface PredictedLabel {
  id: number
  type: PredictedLabelType
  diagnosis: Diagnosis | null
  medicalAct: BaseAct | null
  confidence: number
  promoted: boolean
  automationModeExcluded: boolean
  standardModeExcluded: boolean
  hints?: HintPredictedLabel[]
  predictionModel: PredictionModel
}
export type PredictedDiagnosis = PredictedLabel & {
  diagnosis: Diagnosis
  medicalAct: null
}
export type PredictedMedicalAct = PredictedLabel & {
  diagnosis: null
  medicalAct: BaseAct
}

// todo split LabelType and DiagnosisType
export enum DiagnosisType {
  DP = 'DP',
  DR = 'DR',
  FPP = 'FPP',
  MMP = 'MMP',
  AE = 'AE',
  DAS = 'DAS',
  // singular : legacy suggestedLabel.type value from SuggestedLabelTypeEnum (search suggestions) ;
  // distinct from PredictedLabelType.MEDICAL_ACTS ('medicalActs', used by AI prediction/justification data)
  medicalAct = 'medicalAct',
}

export function getSingleDiagnosisTypes(pmsiType: PmsiTypeEnum, date?: Dayjs): DiagnosisType[] {
  switch (pmsiType) {
    case PmsiTypeEnum.MCO:
      return [
        DiagnosisType.DP,
        DiagnosisType.DR,
      ]
    case PmsiTypeEnum.SMR: {
      const types = [
        DiagnosisType.MMP,
        DiagnosisType.AE,
      ]
      if (date < FPP_DIAGNOSIS_EXPIRATION_DATE) {
        types.unshift(DiagnosisType.FPP)
      }
      return types
    }
    default:
      return [
        DiagnosisType.DP,
        DiagnosisType.DR,
        DiagnosisType.FPP,
        DiagnosisType.MMP,
        DiagnosisType.AE,
      ]
  }
}

export interface Diagnosis {
  id?: number
  reference: string
  type?: number | null
  level?: number
  description?: string
  diagnosisVersions?: DiagnosisVersion[]
  canBeFpp?: boolean | null
  canBeMmp?: boolean | null
  canBeAe?: boolean | null
  canBeDas?: boolean | null
}

export interface SuggestedDiagnosis extends Diagnosis {
  search?: Search | StaySuggestionSearch
}

export interface DiagnosisVersion {
  id: number
  level: number
  // Used internally by rawStayMapper, no need to define generic type for now
  startDate: string
  endDate: string
}

export interface HintPredictedLabel {
  id: number
  hintStart: number
  hintEnd: number
  confidence: number
  targetType: HealthDataType
  targetId: number
}

export class DiagnosisState extends AbstractState {}
